import dayjs from 'dayjs'
import { Modal } from 'react-bootstrap'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  SubmitHandler,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { FileDropZone } from '../../../../components/forms/dropzone/fileDropZone.type'
import { Cargando } from '../../../../components/layout/Cargando'
import { Opcion } from '../../../../models/catalogo.type'
import {
  calcularCantidadElementosPeriodicidad,
  generarNuevoArregloFechas,
} from '../../../../utils/CalcularCantidadElementosPeriodicidad'
import { AppConstantes } from '../../../../utils/Constantes'
import { Condicionante } from '../../models'
import { TCondicionanteForm } from '../../models/condicionanteForm.type'
import { ReportesCumplimiento } from '../reportes/ReportesCumplimiento'
import { DatosCondicionanteModal } from './modalBody/DatosCondicionanteModal'
import { RecordatoriosRecurrentes } from './modalBody/RecordatoriosRecurrentes'
import { TareaCondicionanteModal } from './modalBody/TareaCondicionanteModal'

type Props = {
  isModoEditar: boolean
  isLoadingGuardar: boolean
  isLoadingActualizar: boolean
  isLoadingGuardarReportes: boolean
  usuarioPermiso?: boolean
  fechaVencimientoAutorizacion?: string
  condicionante?: Condicionante
  tabActivo: string
  setTabActivo: (arg: string) => void
  arrArchivos: FileDropZone[]
  setArrArchivos: (arg: FileDropZone[]) => void
  idSitio?: string
  idAutorizacion?: string
  fields: FieldArrayWithId<TCondicionanteForm, 'tareas'>[]
  remove: UseFieldArrayRemove
  append: UseFieldArrayAppend<TCondicionanteForm, 'tareas'>
  control: Control<TCondicionanteForm>
  register: UseFormRegister<TCondicionanteForm>
  errors: FieldErrors<TCondicionanteForm>
  trigger: UseFormTrigger<TCondicionanteForm>
  reset: UseFormReset<TCondicionanteForm>
  getValues: UseFormGetValues<TCondicionanteForm>
  setValue: UseFormSetValue<TCondicionanteForm>
  watch: UseFormWatch<TCondicionanteForm>
  handleSubmit: UseFormHandleSubmit<TCondicionanteForm>
  onSubmit: SubmitHandler<TCondicionanteForm>
  isSuccessTareas: boolean
  isRemoverRecordatorios?: boolean
}

export const ModalCondicionanteBody = ({
  isModoEditar,
  isLoadingGuardar,
  isLoadingActualizar,
  isLoadingGuardarReportes,
  usuarioPermiso,
  fechaVencimientoAutorizacion,
  condicionante,
  tabActivo,
  setTabActivo,
  arrArchivos,
  setArrArchivos,
  idSitio,
  idAutorizacion,
  fields,
  remove,
  append,
  control,
  register,
  errors,
  trigger,
  reset,
  getValues,
  setValue,
  watch,
  handleSubmit,
  onSubmit,
  isSuccessTareas,
  isRemoverRecordatorios = false,
}: Props) => {
  // Body del modal con tabs:
  // - Datos generales
  // - Tareas (manuales o generadas por periodicidad)
  // - Evidencia documental (ReportesCumplimiento)
  // - Recordatorios recurrentes (solo en creacion)
  // Importante: en creacion, cambiar periodicidad/cantidad regenera tareas "no manuales".
  let periodicidadCondicionanteSeleccionada: string
  const cantidadCamposManuales = fields.filter((item) => item.isManual === false).length

  /**
   * @description Borra elementos ingresados automaticmanente
   *              en Tareas
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 18/11/2022
   */
  const borrarElementosTareasNoManuales = () => {
    // Remueve tareas generadas automaticamente por periodicidad.
    // Mantiene las manuales y evita inconsistencias cuando cambian nombre/descripcion/periodicidad.
    const matrizIndices = [-1]
    for (let i = 0; i < fields.length; i++) {
      if (
        fields[i].isManual === false &&
        getValues().tareas[i].descripcion === getValues().descripcion &&
        getValues().tareas[i].idUsuarioAsignado === null &&
        getValues().tareas[i].estatus !== null
      ) {
        matrizIndices.push(i + 1)
      }
    }
    remove(matrizIndices)
  }

  /**
   * @description Calcula la cantidad de tareas en función a la periodicidad
   *              y agrega esa cantida de input sobre el formulario.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 02/11/2022
   * @param e es un parámetro que viene de un catálogo relacionado con la periodicidad
   *        de una condicionante.
   */
  const calcularCantidadTareas = (e: Opcion | undefined) => {
    // Genera el set inicial de tareas basado en periodicidad.
    // El helper devuelve: [cantidad, fechasInicio[], fechasTermino[]].
    if (e) {
      periodicidadCondicionanteSeleccionada = e.valor
      const cantidadElementosPeriodicidad = calcularCantidadElementosPeriodicidad(
        fechaVencimientoAutorizacion,
        getValues('cantidadPeriodos'),
        periodicidadCondicionanteSeleccionada
      )

      for (let i = 0; i < cantidadElementosPeriodicidad[0] + 1; i++) {
        append(
          {
            titulo: `${getValues('nombre')} - ${dayjs(cantidadElementosPeriodicidad[2][i]).format(
              AppConstantes.FORMATO_FECHA
            )}  `,
            descripcion: `${getValues('descripcion')}`,
            estatus: {
              valor: AppConstantes.CVE_ESTATUS_TAREA_EN_PROCESO,
              etiqueta: AppConstantes.DESC_ESTATUS_TAREA_EN_PROCESO,
            },
            fechaInicio: cantidadElementosPeriodicidad[1][i],
            fechaTermino: cantidadElementosPeriodicidad[2][i],
            idUsuarioAsignado: null,
            isManual: false,
          },
          { focusName: 'tareas.0.titulo' }
        )
      }
    }
  }

  /**
   * @description envia los valores de las nuevas fechas de término a los campos de titulo de tareas
   *              al modificar lo de la primera tarea generada por periodicidad.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/12/2023
   */
  const recalcularFechasInicio = () => {
    // Si el usuario mueve la primera tarea generada por periodicidad, se recalculan
    // las fechas del resto para mantener el espaciado acorde a la periodicidad.
    const nuevasFechas = generarNuevoArregloFechas(
      getValues('tareas.0.fechaInicio'),
      getValues('tareas.0.fechaTermino'),
      cantidadCamposManuales,
      getValues('periodicidad.valor'),
      fechaVencimientoAutorizacion
    )
    for (let i = 1; i < cantidadCamposManuales; i++) {
      setValue(`tareas.${i}.fechaInicio`, nuevasFechas[0][i])
      setValue(`tareas.${i}.fechaTermino`, nuevasFechas[1][i])
      setValue(
        `tareas.${i}.titulo`,
        `${getValues('nombre')} - ${dayjs(nuevasFechas[1][i]).format(
          AppConstantes.FORMATO_FECHA
        )}  `
      )
    }
  }
  return (
    <Modal.Body className={`${usuarioPermiso ? '' : 'overlay overlay-allowed'}`}>
      <form id='form-condicionante' onSubmit={handleSubmit(onSubmit)}>
        <div
          className={`${
            isLoadingGuardar || isLoadingActualizar || isLoadingGuardarReportes
              ? 'overlay overlay-block'
              : ''
          }`}>
          <div className='row'>
            <div className='d-flex'>
              <div className='col-12'>
                <DatosCondicionanteModal
                  fechaVencimientoAutorizacion={fechaVencimientoAutorizacion}
                  isModoEditar={isModoEditar}
                  calcularCantidadTareas={calcularCantidadTareas}
                  borrarElementosTareasNoManuales={borrarElementosTareasNoManuales}
                  condicionante={condicionante}
                  control={control}
                  errors={errors}
                  register={register}
                  getValues={getValues}
                  setValue={setValue}
                  usuarioPermiso={usuarioPermiso}></DatosCondicionanteModal>
              </div>
            </div>

            <div className=' row justify-content-center align-items-center mt-6'>
              <ul className=' col-auto nav nav-tabs nav-line-tabs mb-5 fs-6 ms-3'>
                <li className='nav-item'>
                  <a
                    className={`nav-link ${tabActivo === AppConstantes.CVE_TAB_TAREAS && 'active'}`}
                    onClick={() => {
                      setTabActivo(AppConstantes.CVE_TAB_TAREAS)
                    }}
                    data-bs-toggle='tab'
                    href='#tab_tareas'>
                    <h5> Tareas</h5>
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    onClick={() => {
                      setTabActivo(AppConstantes.CVE_TAB_REPORTES)
                    }}
                    className={`nav-link ${
                      tabActivo === AppConstantes.CVE_TAB_REPORTES && 'active'
                    }`}
                    data-bs-toggle='tab'
                    href='#tab_reportes'>
                    <h5> Evidencia documental</h5>
                  </a>
                </li>
                {!isModoEditar && (
                  <li className='nav-item'>
                    <a
                      onClick={() => {
                        setTabActivo(AppConstantes.CVE_TAB_RECORDATORIOS_RECURRENTES)
                      }}
                      className={`nav-link ${
                        tabActivo === AppConstantes.CVE_TAB_RECORDATORIOS_RECURRENTES
                          ? 'active'
                          : null
                      } ${fields.length < 1 ? 'disabled' : null}`}
                      data-bs-toggle='tab'
                      href='#tab_recordatorios'>
                      <h5 className={`${fields.length < 1 ? 'text-gray-500' : null}`}>
                        Recordatorios recurrentes
                      </h5>
                    </a>
                  </li>
                )}
              </ul>
            </div>

            <div className='tab-content pr-0' id='myTabContent' style={{ paddingRight: '0px' }}>
              <div
                className={`tab-pane fade ${
                  tabActivo === AppConstantes.CVE_TAB_TAREAS && 'show active'
                }  `}
                id='tab_tareas'
                role='tabpanel'>
                <div className='col-12 flex-column' id='div-modal-condicionante'>
                  <TareaCondicionanteModal
                    idSitio={idSitio}
                    idAutorizacion={idAutorizacion}
                    idCondicionante={condicionante?.idCondicionante}
                    isModoEditar={isModoEditar}
                    fechaVencimiento={fechaVencimientoAutorizacion}
                    fields={fields}
                    remove={remove}
                    append={append}
                    condicionante={condicionante}
                    {...{
                      control,
                      register,
                      errors,
                      getValues,
                      watch,
                      setValue,
                      trigger,
                      reset,
                    }}
                    recalcularFechasInicio={recalcularFechasInicio}
                    isSuccessTareas={isSuccessTareas}></TareaCondicionanteModal>
                </div>
              </div>
              <div
                className={`tab-pane fade ${
                  tabActivo === AppConstantes.CVE_TAB_REPORTES && 'show active'
                }  `}
                id='tab_reportes'
                role='tabpanel'>
                <div className='col-12 mb-3 d-flex justify-content-center'>
                  {/* REPORTES */}
                  <div className={`${usuarioPermiso ? 'col-8' : 'col-3'}`}>
                    <ReportesCumplimiento
                      arrArchivos={arrArchivos}
                      setArrArchivos={setArrArchivos}
                      idRelacionado={condicionante?.idCondicionante ?? ''}
                      height={400}
                      width={'100%'}
                      heightMaxScroll={300}
                      textoTipoArchivo={'No hay evidencia documental'}
                      usuarioPermisoCrear={usuarioPermiso}
                    />
                  </div>
                </div>
              </div>
                {!isModoEditar && (
                  <RecordatoriosRecurrentes
                  {...{
                    tabActivo,
                    control,
                    errors,
                    getValues,
                    isModoEditar,
                    register,
                    trigger,
                    watch,
                  }}
                  tipo='AUTORIZACION'
                    isRemoverRecordatorios={isRemoverRecordatorios}
                 />
                )}
            </div>
          </div>

          {(isLoadingGuardar || isLoadingActualizar || isLoadingGuardarReportes) && (
            <Cargando mensaje='Guardando condicionante' />
          )}
        </div>
      </form>
    </Modal.Body>
  )
}
